import React, { useMemo, useState } from "react";
import {
  Award,
  BadgeCheck,
  Clock,
  Shield,
  ThumbsUp,
  Users,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { ImageLightbox } from "../../../components/ui/ImageLightbox";
import { ImageWithFallback } from "../../../components/ui/ImageWithFallback";
import { Section, SectionHeader } from "../../../components/ui/section";
import { cn } from "../../../lib/utils";
import {
  CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
  CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
} from "../layoutRadius";
import type { AboutImage } from "../types";

function aboutPhotoAlt(
  photo: AboutImage,
  photoIndex: number,
  ownerName?: string,
): string {
  return (
    photo.caption?.trim() ||
    (ownerName
      ? `Photo ${photoIndex + 1} — ${ownerName}`
      : `About photo ${photoIndex + 1}`)
  );
}

type HighlightsSectionProps = {
  highlights: string[];
  aboutBlurb?: string;
  ownerProfilePhotoUrl?: string | null;
  ownerName?: string;
  aboutImages?: AboutImage[];
};

const ICONS = [Shield, Clock, Award, ThumbsUp, Users, BadgeCheck];

export const HighlightsSection: React.FC<HighlightsSectionProps> = ({
  highlights,
  aboutBlurb,
  ownerProfilePhotoUrl,
  ownerName,
  aboutImages = [],
}) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const hasHighlights = highlights.length > 0;
  const hasAbout = Boolean(aboutBlurb && aboutBlurb.trim().length > 0);
  const hasAboutImages = aboutImages.length > 0;

  const aboutLightboxImages = useMemo(
    () =>
      aboutImages.map((photo, photoIndex) => ({
        src: photo.largeUrl || photo.imageUrl,
        alt: aboutPhotoAlt(photo, photoIndex, ownerName),
      })),
    [aboutImages, ownerName],
  );

  if (!hasHighlights && !hasAbout && !hasAboutImages) {
    return null;
  }

  return (
    <>
      <Section id="about" className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-16 flex flex-col items-center gap-6">
            {ownerProfilePhotoUrl ? (
              <img
                src={ownerProfilePhotoUrl}
                alt={ownerName ? `Photo of ${ownerName}` : ""}
                className="h-24 w-24 rounded-full border-2 border-border object-cover shadow-md"
              />
            ) : null}
            <SectionHeader
              center
              className={ownerProfilePhotoUrl ? "mb-0" : undefined}
              title="About Us"
              description={
                hasAbout
                  ? aboutBlurb
                  : "Proven local contractor support with results you can see."
              }
            />
          </div>

          {hasAboutImages ? (
            <ul className="mb-16 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:gap-6">
              {aboutImages.map((photo, photoIndex) => (
                <li key={photo.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setLightboxIndex(photoIndex)}
                    className={cn(
                      "group relative aspect-4/3 h-auto w-full overflow-hidden border border-border bg-card p-0 shadow-sm hover:bg-transparent focus:ring-2 focus:ring-accent focus:ring-offset-2",
                      CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
                    )}
                  >
                    <ImageWithFallback
                      src={photo.imageUrl || photo.thumbUrl}
                      alt={aboutPhotoAlt(photo, photoIndex, ownerName)}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}

          {hasHighlights ? (
            <>
              <div className="mb-16 grid grid-cols-2 gap-6 lg:grid-cols-4">
                {[
                  { number: "500+", label: "Projects Completed" },
                  { number: "15+", label: "Years in Business" },
                  { number: "200+", label: "Happy Clients" },
                  { number: "100%", label: "Satisfaction Focus" },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className={cn(
                      "border border-border bg-card px-4 py-6 text-center shadow-sm",
                      CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
                    )}
                  >
                    <div className="mb-1 font-display text-3xl font-semibold text-accent sm:text-4xl">
                      {stat.number}
                    </div>
                    <div className="text-sm font-medium text-muted-foreground">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>

              <ul className="grid gap-6 lg:grid-cols-3 lg:gap-8">
                {highlights.map((highlight, index) => {
                  const Icon = ICONS[index % ICONS.length]!;
                  return (
                    <li key={`${highlight}-${index}`}>
                      <div
                        className={cn(
                          "flex gap-4 border border-border bg-card p-6 transition-colors hover:border-accent/35",
                          CONTRACTOR_SURFACE_RADIUS_LG_CLASS,
                        )}
                      >
                        <div
                          className={cn(
                            "flex h-11 w-11 shrink-0 items-center justify-center bg-accent/10",
                            CONTRACTOR_SURFACE_RADIUS_MD_CLASS,
                          )}
                        >
                          <Icon className="h-5 w-5 text-accent" />
                        </div>
                        <div>
                          <h3 className="mb-1.5 font-display text-base font-semibold text-foreground">
                            {highlight}
                          </h3>
                          <p className="text-sm leading-relaxed text-muted-foreground">
                            Reliable planning, clear communication, and
                            detail-focused execution for every job.
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
        </div>
      </Section>

      {lightboxIndex !== null && hasAboutImages ? (
        <ImageLightbox
          images={aboutLightboxImages}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
          imageClassName={CONTRACTOR_SURFACE_RADIUS_MD_CLASS}
        />
      ) : null}
    </>
  );
};
